import Vue from 'vue'
import config from './config.js'
import RoutesPaths from 'src/routes/routesPaths'

class AuthService {
  // Local storage keys
  static ACCESS_TOKEN = 'ACCESS_TOKEN'
  static REFRESH_TOKEN = 'REFRESH_TOKEN'
  static TOKEN_EXPIRATION_TIME = 'TOKEN_EXPIRATION_TIME'

  _authBase64Data = null

  _requestPath = '/oauth'

  _currentAccessToken = null
  _currentRefreshToken = null

  constructor (apiUrl) {
    this._apiUrl = apiUrl
  }

  isAuthenticated () {
    return this._currentAccessToken !== null && !this._tokenExpired()
  }

  async logout () {
    // TODO enviar el request para invalidar el token actual
    await this.revokeToken()
    this._setAuthentication(null)
  }

  async login (username, password) {
    this._authBase64Data = btoa(`${username}:${password}`)
    const response = await Vue.http.post(
      `${this._apiUrl}${this._requestPath}/token`,
      'grant_type=client_credentials&scope=all',
      { headers: this._getAuthenticationHeaders(this._authBase64Data) }
    )

    this._setAuthentication(response.body)
    return response.body
  }

  async refreshToken () {
    const responseRefreshToken = await Vue.http.post(
      `${this._apiUrl}${this._requestPath}/token`,
      `grant_type=refresh_token&refresh_token=${this._currentRefreshToken}`,
      { headers: this._getAuthenticationHeaders(this._authBase64Data) }
    )
    this._setAuthentication(responseRefreshToken.body)
  }

  async revokeToken () {
    try {
      await Vue.http.post(
        `${this._apiUrl}${this._requestPath}/revoke`,
        `token=${this._currentRefreshToken}&token_type_hint=refresh_token`,
        { headers: this._getAuthenticationHeaders(this._authBase64Data) }
      )
    } catch (error) {
      console.log(error)
      console.log('Sesion Finalizada')
    } finally {
      this._unsetAuthentication()
    }
  }

  redirectToLoginIfNeeded (router) {
    this._restoreAuthentication()
    if (!this.isAuthenticated()) {
      alert('Su sesión ha expirado.')
      router.push(RoutesPaths.Login)
    }
  }

  /**
   * @description Indicates token expiration based on time difference between initial time and
   * current time
   *
   * @returns Boolean
   */
  _tokenExpired () {
    const now = (new Date()).getTime()
    const expirationTime = localStorage.getItem(AuthService.TOKEN_EXPIRATION_TIME)
    return expirationTime == null || now >= expirationTime
  }

  /**
   * @description Sets up the needed data for authentication
   *
   * @returns undefined
   */
  _setAuthentication (authentication) {
    this._setupHeaders(authentication.access_token)
    const expirationTime = (new Date()).getTime() + authentication.expires_in * 1000

    this._currentAccessToken = authentication.access_token
    this._currentRefreshToken = authentication.refresh_token
    localStorage.setItem(AuthService.ACCESS_TOKEN, authentication.access_token)
    localStorage.setItem(AuthService.REFRESH_TOKEN, authentication.refresh_token)
    localStorage.setItem(AuthService.TOKEN_EXPIRATION_TIME, expirationTime)
  }

  /**
   * @description Unsets authentication data
   *
   * @returns undefined
   */
  _unsetAuthentication () {
    this._currentAccessToken = null
    this._currentRefreshToken = null
    localStorage.removeItem(AuthService.ACCESS_TOKEN)
    localStorage.removeItem(AuthService.REFRESH_TOKEN)
    localStorage.removeItem(AuthService.TOKEN_EXPIRATION_TIME)
  }

  /**
   * @description Gets the headers for doing authentication
   *
   * @param {Username and password encoded in Base64} authBase64Data
   *
   * @returns Authentication request headers
   */
  _getAuthenticationHeaders (authBase64Data) {
    return {
      'Content-Type': 'application/x-www-form-urlencoded; charset=utf-8',
      'Authorization': `Basic ${authBase64Data}`
    }
  }

  /**
   * @description Restores auth tokens from localStorage
   *
   * @returns undefined
   */
  _restoreAuthentication () {
    if (this._tokenExpired()) {
      return
    }

    const accessToken = localStorage.getItem(AuthService.ACCESS_TOKEN)
    const refreshToken = localStorage.getItem(AuthService.REFRESH_TOKEN)
    if (accessToken != null) {
      this._currentAccessToken = accessToken
      this._currentRefreshToken = refreshToken
      this._setupHeaders(accessToken)
    }
  }

  /**
   * @description Sets up the requests headers
   *
   * @returns undefined
  */
  _setupHeaders (accessToken) {
    Vue.http.headers.common['Authorization'] = `Bearer ${accessToken}`
    Vue.http.headers.common['Accept'] = 'application/json; charset=utf-8'
    Vue.http.headers.common['Content-Type'] = 'application/json'
  }
}

var auth = new AuthService(config.apiUrl())
export default auth



// WEBPACK FOOTER //
// ./src/authService.js